import { inputAdornmentClasses, inputBaseClasses, styled } from "@mui/material";

import { fixSsrWarning } from "fond/utils/css";

import TextField from "./TextField";

/**
 * A styled version of the FOND TextField component that allows for
 * autofill styles to show together with custom adornments.
 *
 * Use this component when you want to have an input field that supports autofill,
 * like names, emails, passwords, etc., and you want to also add adornments like icons or icon buttons.
 *
 * It works by moving the adornments into the input field's padding area, rather than displaying
 * them side by side. This allows for a cleaner look and better integration with autofill styles.
 *
 * If you have adornments that are wide (> spacing * 5), you may need to ensure adequate padding is set.
 */
export const AutofillTextField = styled(TextField)`
  .${inputBaseClasses.root} {
    --input-padding-left: ${({ theme }) => theme.spacing(1.5)};
    --input-padding-right: ${({ theme }) => theme.spacing(1.5)};
    /*
      the container offset is somewhat predicated on the content being an icon button
      and so has its own internal padding. If there's a different type of content it
      may need to compensate for that.
    */
    --adornment-container-offset: ${({ theme }) => theme.spacing(1)};
    --adornment-svg-only-offset: ${({ theme }) => theme.spacing(1)};
    position: relative;
    padding-inline: 0;
    display: inline-block;

    &.${inputBaseClasses.adornedStart}, &.${inputBaseClasses.adornedEnd} {
      /*
       * input adornment containers are (effectively) 0-height,
       * so only need to be positioned 50% down to be centred
       */
      .${inputAdornmentClasses.positionStart}, & > svg:first-child ${fixSsrWarning}, .${inputAdornmentClasses.positionEnd}, & > input ~ svg {
        position: absolute;
        top: 50%;
      }

      /* the svg only versions also need a negative margin to be positioned correctly */
      & > input ~ svg,
      & > svg:first-child ${fixSsrWarning} {
        margin-top: -0.5em;
      }
    }

    &.${inputBaseClasses.adornedStart} {
      --input-padding-left: ${({ theme }) => theme.spacing(5)};

      .${inputAdornmentClasses.positionStart}, & > svg:first-child ${fixSsrWarning} {
        left: ${({ theme }) => theme.spacing(1)};
      }
    }

    &.${inputBaseClasses.adornedEnd} {
      --input-padding-right: ${({ theme }) => theme.spacing(5)};

      .${inputAdornmentClasses.positionEnd}, & > input ~ svg {
        right: ${({ theme }) => theme.spacing(1)};
      }
    }

    .${inputBaseClasses.input} {
      padding-left: var(--input-padding-left);
      padding-right: var(--input-padding-right);
      width: calc(100% - var(--input-padding-left) - var(--input-padding-right));
    }
  }
`;
